import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  Box,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { useBookingStore } from '../store/bookingStore';
import { TimeSlot, PaymentMethod } from '../types';
import { CreditCard, Wallet } from 'lucide-react';

interface BookingModalProps {
  open: boolean;
  onClose: () => void;
}

const steps = ['Select Date & Time', 'Guest Information', 'Payment Method'];

export const BookingModal: React.FC<BookingModalProps> = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<string[]>([]);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [numberOfGuests, setNumberOfGuests] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('pay_later');

  const { tables, getAvailableTimeSlots, addBooking }: any = useBookingStore();
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date && selectedTable) {
      setTimeSlots(getAvailableTimeSlots(date, selectedTable));
    }
  };

  const handleTableChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const tableId = event.target.value as string;
    setSelectedTable(tableId);
    if (selectedDate) {
      setTimeSlots(getAvailableTimeSlots(selectedDate, tableId));
    }
  };

  const handleTimeSlotSelection = (
    event: React.MouseEvent<HTMLElement>,
    newSlots: string[]
  ) => {
    setSelectedTimeSlots(newSlots);
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    if (selectedDate && selectedTable && selectedTimeSlots.length > 0) {
      addBooking({
        tableId: selectedTable,
        date: format(selectedDate, 'yyyy-MM-dd'),
        timeSlots: selectedTimeSlots,
        customerName,
        customerPhone,
        numberOfGuests,
        paymentMethod,
      });
      onClose();
      setActiveStep(0);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={handleDateChange}
                />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Table</InputLabel>
                  <Select value={selectedTable} onChange={(e:any) => handleTableChange(e)}>
                    {tables.map((table: any) => (
                      <MenuItem key={table.id} value={table.id}>
                        Table {table.number} ({table.category}) - {table.capacity}{' '}
                        seats
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Available Time Slots</Typography>
                <ToggleButtonGroup
                  value={selectedTimeSlots}
                  onChange={handleTimeSlotSelection}
                  aria-label="time slots"
                  // multiple
                >
                  {timeSlots.map((slot) => (
                    <ToggleButton
                      key={slot.start}
                      value={slot.start}
                      disabled={!slot.isAvailable}
                    >
                      {slot.start} - {slot.end}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
            </Grid>
          </>
        );
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Number of Guests"
                value={numberOfGuests}
                onChange={(e) => setNumberOfGuests(Math.max(1, parseInt(e.target.value)))}
                inputProps={{ min: 1, max: 20 }}
                helperText="Please enter the number of guests (1-20)"
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Select Payment Method
            </Typography>
            <RadioGroup
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={paymentMethod === 'pay_later' ? 8 : 1}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      border: paymentMethod === 'pay_later' ? '2px solid #1976d2' : '2px solid transparent',
                    }}
                    onClick={() => setPaymentMethod('pay_later')}
                  >
                    <FormControlLabel
                      value="pay_later"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Wallet size={24} />
                          <Box>
                            <Typography variant="subtitle1">Pay Later</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Pay at the restaurant
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      opacity: 0.5,
                      cursor: 'not-allowed',
                    }}
                  >
                    <FormControlLabel
                      value="online"
                      disabled
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CreditCard size={24} />
                          <Box>
                            <Typography variant="subtitle1">Online Payment</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Coming soon
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>
              </Grid>
            </RadioGroup>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Book a Table</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mt: 2, mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {getStepContent(activeStep)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {activeStep > 0 && <Button onClick={handleBack}>Back</Button>}
        {activeStep === steps.length - 1 ? (
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Submit
          </Button>
        ) : (
          <Button onClick={handleNext} variant="contained" color="primary">
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};